
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { supabase } from '@/integrations/supabase/client';

const StatusDonuts = () => {
  const { t } = useLanguage();

  const { data, isLoading } = useQuery({
    queryKey: ['status-donuts'],
    queryFn: async () => {
      // Booking Status
      const { data: bookings } = await supabase
        .from('bookings')
        .select('status');

      const bookingStats = bookings?.reduce((acc, booking) => {
        acc[booking.status] = (acc[booking.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // KYC Status
      const { data: profiles } = await supabase
        .from('profiles')
        .select('kyc_status');

      const kycStats = profiles?.reduce((acc, profile) => {
        acc[profile.kyc_status] = (acc[profile.kyc_status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Listing Status
      const { data: listings } = await supabase
        .from('listings')
        .select('status');

      const listingStats = listings?.reduce((acc, listing) => {
        acc[listing.status] = (acc[listing.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      return {
        bookings: Object.entries(bookingStats).map(([name, value]) => ({ name, value })),
        kyc: Object.entries(kycStats).map(([name, value]) => ({ name, value })),
        listings: Object.entries(listingStats).map(([name, value]) => ({ name, value }))
      };
    }
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const renderPieChart = (data: any[], title: string) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {renderPieChart(data?.bookings || [], t('booking_status', 'حالة الحجز', 'Booking Status'))}
      {renderPieChart(data?.kyc || [], t('kyc_status', 'حالة التحقق', 'KYC Status'))}
      {renderPieChart(data?.listings || [], t('listing_status', 'حالة القائمة', 'Listing Status'))}
    </div>
  );
};

export default StatusDonuts;
